import { Controller } from "@hotwired/stimulus"
import { pluralize, metric, dynos } from "../../lib/helpers"
import { reactor } from "../../lib/reactor"

export default class extends Controller {
  static targets = ["instructionsFrame"]

  static values = {
    // The strategy's metric, which picks the live field copy:
    // rqt, jql, jqs, cpu, rpm, response, connect, load, or apdex.
    metricKind: String,
    // The add-on strategy's wakes_from_zero?. Heroku passes none: there the process name decides.
    wakesFromZero: Boolean,
    language: { type: String, default: "ruby" },
    rubyFramework: String,
    pythonFramework: String,
    nodeFramework: String,
    // Instructions::Catalog::DEFAULT_FRAMEWORK, by language.
    defaultFrameworks: { type: Object, default: {} },
    // Class tokens from AutoscalersHelper (instruction_card_*_classes).
    cardRuntimeActiveClass: String,
    cardFrameworkActiveClass: String,
    cardInactiveClass: String,
    name: { type: String, default: "" },
    // Per-library facts from the server (job_adapter_facts): label, language, queuesRequired, countsRunning.
    adapters: { type: Object, default: {} },
    // Collection worker library (wire key). Bound from the adapter select.
    adapter: { type: String, default: "" },
    // False until a pick on the Heroku new form. A blank adapter then is not Custom yet.
    adapterPicked: { type: Boolean, default: true },
    // Heroku: the application reports a 1.x agent, which ignores a selected library.
    legacyAgent: { type: Boolean, default: false },
    // Heroku Job Latency hold notes. {library} in the second is the selected library's label.
    latencyHoldUnlockNote: String,
    latencyHoldUnavailableNote: String,
    // The library whose macro the Heroku setup steps show. It is never submitted.
    library: { type: String, default: "" },
    // The hirefire-resource major (1 or 2) whose macro steps the Heroku form shows. Blank is the server's pick.
    version: { type: String, default: "" },
    // Integration Turbo Frame compose endpoint (path only).
    instructionsUrl: { type: String, default: "" },
    minimum: Number,
    maximum: Number,
    thresholdDown: Number,
    thresholdUp: Number,
    quantityDown: Number,
    quantityUp: Number,
    sensitivityDown: Number,
    sensitivityUp: Number,
    cooldownDown: Number,
    cooldownUp: Number,
    checkupFrequency: { type: Number, default: 15 },
    // Add-on cooldowns are seconds. Heroku manager timeouts are minutes.
    cooldownUnit: { type: String, default: "seconds" },
    ratio: Number,
    limitDown: Number,
    limitUp: Number,
    aggregation: String,
    percentile: Number,
    lastMinutes: Number,
  }

  // Field keys a threshold/sensitivity preset button may declare via data-*-param.
  static PRESET_FIELDS = [
    "minimum",
    "thresholdDown",
    "thresholdUp",
    "quantityDown",
    "quantityUp",
    "sensitivityDown",
    "sensitivityUp",
    "cooldownDown",
    "cooldownUp",
  ]

  connect() {
    this.boundSyncPresetHighlight = this.#syncPresetHighlight.bind(this)
    this.instructionsReady = false
    reactor(this)
    // Recompute chip highlight whenever any model field input fires (option 2).
    this.element.addEventListener("input", this.boundSyncPresetHighlight)
    requestAnimationFrame(() => {
      this.boundSyncPresetHighlight()
      this.#syncProcessTypeDropdown()
      this.#syncAdapterDropdown()
      // Avoid adapter/name valueChanged reloads racing the initial frame src.
      this.instructionsReady = true
      this.#reloadInstructionsIfStale()
    })
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundSyncPresetHighlight)
  }

  morph() {
    this.#syncPresetHighlight()
    this.#syncAdapterDropdown()
  }

  // Queues only apply when a worker library is set (Custom configuration uses app code).
  adapterSelected() {
    return this.adapterPickedValue && Boolean(this.adapterValue)
  }

  legacyLibrary() {
    return this.legacyAgentValue && this.adapterSelected()
  }

  // Heroku Job Latency: the hold reads the running count, which a 2.x agent sends for these libraries only.
  latencyHoldAvailable() {
    return !this.legacyAgentValue && this.adapterSelected() && this.holdWhileWorkingSupported()
  }

  // A library that sends no running count wins over the upgrade note.
  latencyHoldNote() {
    if (this.adapterSelected() && !this.holdWhileWorkingSupported()) {
      const name = this.#adapterFacts().label || this.adapterValue
      return this.latencyHoldUnavailableNoteValue.replace("{library}", name)
    }
    return this.latencyHoldUnlockNoteValue
  }

  // Heroku Job Queue: these two count waiting jobs only.
  waitingOnlyHold() {
    return this.adapterSelected() && this.#adapterFacts().countsRunning === false
  }

  // Long-running jobs section: only when the selected adapter emits trustworthy wrk.
  holdWhileWorkingSupported() {
    return this.#adapterFacts().countsRunning === true
  }

  #adapterFacts() {
    return this.adaptersValue[this.adapterValue] || {}
  }

  // Collection queues field help, tailored to the selected worker library (HTML for highlights).
  queuesHelpDescription() {
    const howTo =
      "Type a name and press Enter, Tab, or a comma to add it (spaces are allowed inside a name)."
    const adapter = this.adapterValue || ""
    if (!adapter) return ""

    const name = this.#adapterFacts().label || adapter

    if (this.#adapterFacts().queuesRequired) {
      return `${name} requires at least one queue name. ${howTo}`
    }

    const emptyHint = `<strong class="font-semibold text-gray-900 dark:text-white">Leave empty to monitor all queues.</strong>`
    return `Queue names this process works on for ${name}. ${howTo} ${emptyHint}`
  }

  selectLanguage({ params: { language }, currentTarget }) {
    this.languageValue = language
    this.#useDefaultFramework(language)
    if (currentTarget) {
      this.#updateCardStyles(
        currentTarget.closest("[data-language-container]"),
        currentTarget,
        "[data-language-tab]",
        this.cardRuntimeActiveClassValue
      )
    }
    this.#reloadInstructions()
  }

  selectFramework({ params: { group, value }, currentTarget }) {
    this[`${group}Value`] = value
    // Tab group is rubyFramework / pythonFramework / nodeFramework.
    if (typeof group === "string" && group.endsWith("Framework")) {
      this.languageValue = group.slice(0, -"Framework".length)
    }
    if (currentTarget) {
      this.#updateCardStyles(
        currentTarget.closest("[data-fw-container]"),
        currentTarget,
        "[data-fw-tab]",
        this.cardFrameworkActiveClassValue
      )
    }
    this.#reloadInstructions()
  }

  adapterValueChanged() {
    this.#syncLanguageFromAdapter()
    this.#syncAdapterDropdown()
    this.#reloadInstructions()
  }

  adapterPickedValueChanged() {
    this.#syncAdapterDropdown()
  }

  // Worker Library pick (rich-dropdown:change). A blank key is Custom.
  chooseAdapter({ detail: { key } }) {
    // A first pick of Custom leaves the adapter blank, so no value change would load the steps.
    const unchanged = !this.adapterPickedValue && key === (this.adapterValue || "")
    this.adapterPickedValue = true
    this.#setModelField("adapter", key)
    if (unchanged) this.#reloadInstructions()
  }

  selectLibrary({ params: { library } }) {
    this.libraryValue = library
  }

  libraryValueChanged() {
    this.#reloadInstructions()
  }

  selectVersion({ params: { version } }) {
    this.versionValue = String(version)
  }

  versionValueChanged() {
    this.#reloadInstructions()
  }

  // Process Type pick (rich-dropdown:change). Custom opens the free-text input.
  chooseProcessType({ detail: { key, custom } }) {
    this.#setModelField("name", custom ? "" : key)
    this.#syncProcessTypeDropdown()
    if (!custom) return

    const input = this.element.querySelector("[data-autoscaler--form-process-type-custom-input]")
    if (input) {
      input.value = ""
      input.focus()
    }
  }

  customProcessTypeInput({ currentTarget }) {
    const value = currentTarget?.value || ""
    this.#setModelField("name", value)
    this.#syncProcessTypeDropdown()
  }

  // The name selects its option, or Custom when the formation list has no such process.
  #syncProcessTypeDropdown() {
    const group = this.element.querySelector("[data-autoscaler--form-process-type-group]")
    if (!group) return

    const name = this.nameValue || ""
    this.#showOption(group, name)
    const option = group.querySelector(`[data-process-type-option="${CSS.escape(name)}"]`)
    const shown = option || group.querySelector("[data-rich-dropdown-custom]")
    this.#syncProcessTypeTriggerCue(group, shown?.getAttribute("data-process-type-cue") || "worker")

    const customWrap = this.element.querySelector("[data-autoscaler--form-process-type-custom]")
    if (customWrap) customWrap.classList.toggle("hidden", Boolean(option))

    const customInput = this.element.querySelector("[data-autoscaler--form-process-type-custom-input]")
    if (customInput && !option && customInput.value !== name) customInput.value = name
  }

  // Swap the closed-trigger hex cue (Web vs Worker) from active-tone templates
  // (siblings of the rich-dropdown group, under the form controller element).
  #syncProcessTypeTriggerCue(group, cueKind) {
    const kind = cueKind === "web" ? "web" : "worker"
    const source = this.element.querySelector(
      `[data-process-type-cue-source="${kind}"] [data-rich-dropdown-cue]`
    )
    const trigger = group.querySelector(".rich-dropdown-trigger")
    const triggerCue = trigger?.querySelector("[data-rich-dropdown-cue]")
    if (!source || !triggerCue) return
    if (triggerCue.getAttribute("data-process-type-cue") === kind) return
    triggerCue.replaceWith(source.cloneNode(true))
  }

  // Not picked keeps the list and the placeholder trigger the server rendered.
  #syncAdapterDropdown() {
    const group = this.element.querySelector("[data-autoscaler--form-adapter-group]")
    if (!group) return

    const picked = this.adapterPickedValue
    const field = this.element.querySelector("input[type='hidden'][data-autoscaler--form-model='adapter']")
    if (field) field.disabled = !picked
    if (!picked) return

    const key = this.adapterValue || ""
    this.#showOption(group, key)
    const option = group.querySelector(`[data-adapter-option="${CSS.escape(key)}"]`)
    const logo = group.querySelector("[data-rich-dropdown-trigger-logo]")
    if (!option || !logo) return

    logo.setAttribute("src", option.dataset.richDropdownOptionLogo)
    const invert = option.dataset.richDropdownOptionInvert === "true"
    logo.classList.toggle("dark:brightness-0", invert)
    logo.classList.toggle("dark:invert", invert)
  }

  // Tells the group's rich-dropdown which option is current. A key no option has is Custom.
  #showOption(group, key) {
    this.dispatch("show", { target: group, prefix: "rich-dropdown", detail: { key }, bubbles: false })
  }

  nameValueChanged() {
    this.#syncProcessTypeDropdown()
    this.#reloadInstructions()
  }

  #syncLanguageFromAdapter() {
    const adapter = this.adapterValue
    if (!adapter) return

    const lang = this.#adapterFacts().language
    if (!lang) return

    this.languageValue = lang
    this.#useDefaultFramework(lang)
  }

  #useDefaultFramework(language) {
    this[`${language}FrameworkValue`] = this.defaultFrameworksValue[language]
  }

  #currentFramework() {
    const language = this.languageValue
    return this[`${language}FrameworkValue`] || this.defaultFrameworksValue[language]
  }

  // Picks before the ready gate leave src on the default adapter.
  #reloadInstructionsIfStale() {
    if (!this.hasInstructionsFrameTarget) return
    const src = this.instructionsFrameTarget.getAttribute("src") || this.instructionsFrameTarget.dataset.src
    if (!src) return

    const loaded = new URL(src, window.location.origin).searchParams.get("adapter") || ""
    const wanted = this.adapterValue || ""
    if (loaded !== wanted) this.#reloadInstructions()
  }

  #reloadInstructions() {
    if (!this.instructionsReady) return
    if (!this.hasInstructionsFrameTarget) return
    // Heroku shows no steps until a Worker Library or Custom is picked.
    if (!this.adapterPickedValue) return
    const base = this.instructionsUrlValue
    if (!base) return

    const url = new URL(base, window.location.origin)
    url.searchParams.set("language", this.languageValue)
    url.searchParams.set("framework", this.#currentFramework())
    // Blank is Custom, sent explicitly so a server default does not replace it.
    url.searchParams.set("adapter", this.adapterValue || "")
    if (this.libraryValue) url.searchParams.set("library", this.libraryValue)
    if (this.versionValue) url.searchParams.set("version", this.versionValue)
    const name = (this.nameValue || "").trim()
    if (name) url.searchParams.set("name", name)
    else url.searchParams.delete("name")
    this.#keepConfiguration(url)

    this.instructionsFrameTarget.src = url.pathname + url.search
  }

  // Configurator queues and options (instructions--configurator) belong to one worker library.
  #keepConfiguration(url) {
    const src = this.instructionsFrameTarget.src || this.instructionsFrameTarget.dataset.src
    if (!src) return

    const current = new URL(src, window.location.origin)
    const same = (key) => (current.searchParams.get(key) || "") === (url.searchParams.get(key) || "")
    if (!same("adapter") || !same("library")) return

    for (const [key, value] of current.searchParams) {
      if (key === "queues" || key.startsWith("macro[")) url.searchParams.set(key, value)
    }
  }

  // Add-on tile columns only. Next chevron pickers are redrawn by the frame reload.
  #updateCardStyles(container, activeTab, selector, activeClassString) {
    if (!container) return
    const active = activeClassString.split(/\s+/).filter(Boolean)
    const inactive = this.cardInactiveClassValue.split(/\s+/).filter(Boolean)
    container.querySelectorAll(selector).forEach(tab => {
      if (tab === activeTab) {
        tab.classList.remove(...inactive)
        tab.classList.add(...active)
      } else {
        tab.classList.remove(...active)
        tab.classList.add(...inactive)
      }
    })
  }

  // Matches Instructions::Snippets.ruby_symbol: quoted (and escaped) when not a bare symbol.
  instanceName() {
    const name = this.nameValue.trim() || "worker"
    return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name) ? `:${name}` : `:${JSON.stringify(name)}`
  }

  instanceNameRaw() {
    const name = this.nameValue.trim()
    return name ? name : "worker"
  }

  // Heroku decides by the process name, which this form can change (Manager#wakes_from_zero?).
  #wakesFromZero() {
    if (this.hasWakesFromZeroValue) return this.wakesFromZeroValue

    return this.nameValue.trim() !== "web"
  }

  off() {
    return this.minimumValue === 0 && this.maximumValue === 0
  }

  // Heroku pauses every worker manager on the app while web is off.
  webOff() {
    return this.off() && !this.hasWakesFromZeroValue && !this.#wakesFromZero()
  }

  #offDescription() {
    return `Off. HireFire keeps this process at ${dynos("0 dynos")}.`
  }

  minimumDescription() {
    if (this.off()) return this.#offDescription()

    if (this.minimumValue === 0 && !this.#wakesFromZero())
      return `This process can't scale up from ${dynos("0 dynos")}. Set Maximum to 0 as well to turn it off.`

    if (this.minimumValue === 0)
      return `Can scale down to ${dynos("0 dynos")}.`

    return `Won't scale below ${dynos(pluralize(this.minimumValue, "{{n}} dyno", "{{n}} dynos"))}.`
  }

  maximumDescription() {
    if (this.off()) return this.#offDescription()

    return `Won't scale above ${dynos(pluralize(this.maximumValue, "{{n}} dyno", "{{n}} dynos"))}.`
  }

  thresholdDownDescription() {
    switch (this.metricKindValue) {
      case "rqt":
        return `Scale down when queue time drops below ${metric(`${this.thresholdDownValue} ms`)}.`
      case "cpu":
        return `Scale down when CPU drops below ${metric(`${this.thresholdDownValue}%`)}.`
      case "jql":
        return `Scale down when latency drops below ${metric(pluralize(this.thresholdDownValue, "{{n}} second", "{{n}} seconds"))}.`
      case "response":
        return `Scale down when response time drops below ${metric(`${this.thresholdDownValue} ms`)}.`
      case "connect":
        return `Scale down when connect time drops below ${metric(`${this.thresholdDownValue} ms`)}.`
      case "apdex":
        return `Scale up when Apdex drops below ${metric(this.#hundredths(this.thresholdDownValue))}.`
      case "load":
        return `Scale down when load drops below ${metric(this.#hundredths(this.thresholdDownValue))}.`
      default:
        return `Scale down when latency drops below ${metric(pluralize(this.thresholdDownValue, "{{n}} second", "{{n}} seconds"))}.`
    }
  }

  thresholdUpDescription() {
    switch (this.metricKindValue) {
      case "rqt":
        return `Scale up when queue time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`
      case "cpu":
        return `Scale up when CPU exceeds ${metric(`${this.thresholdUpValue}%`)}.`
      case "jql":
        return `Scale up when latency exceeds ${metric(pluralize(this.thresholdUpValue, "{{n}} second", "{{n}} seconds"))}.`
      case "response":
        return `Scale up when response time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`
      case "connect":
        return `Scale up when connect time exceeds ${metric(`${this.thresholdUpValue} ms`)}.`
      case "apdex":
        return `Scale down when Apdex reaches ${metric(this.#hundredths(this.thresholdUpValue))}.`
      case "load":
        return `Scale up when load exceeds ${metric(this.#hundredths(this.thresholdUpValue))}.`
      default:
        return `Scale up when latency exceeds ${metric(pluralize(this.thresholdUpValue, "{{n}} second", "{{n}} seconds"))}.`
    }
  }

  quantityDownDescription() {
    return `Remove ${dynos(pluralize(this.quantityDownValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  quantityUpDescription() {
    return `Add ${dynos(pluralize(this.quantityUpValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  ratioDescription() {
    if (this.metricKindValue === "rpm") {
      if (this.ratioValue === 1)
        return `${dynos("1 dyno")} targets about ${metric("1 request per minute")}.`

      return `${dynos("1 dyno")} targets about ${metric(pluralize(this.ratioValue, "{{n}} request per minute", "{{n}} requests per minute"))}.`
    }

    if (this.ratioValue === 1)
      return `${dynos("1 dyno")} targets about ${metric("1 job")} of backlog. Not the same as concurrency.`

    return `${dynos("1 dyno")} targets about ${metric(pluralize(this.ratioValue, "{{n}} job", "{{n}} jobs"))} of backlog. Not the same as concurrency.`
  }

  showPercentile() {
    return this.aggregationValue === "percentile"
  }

  aggregationDescription() {
    if (this.aggregationValue === "average")
      return `Use the average across samples.`

    return `Use the ${metric(this.#ordinal(this.percentileValue))} percentile across samples.`
  }

  percentileDescription() {
    return `Use the ${metric(this.#ordinal(this.percentileValue))} percentile.`
  }

  lastMinutesDescription() {
    if (this.lastMinutesValue === 1)
      return `Use the ${metric("1 minute")} load average.`

    return `Use the ${metric(`${this.lastMinutesValue} minute`)} load average.`
  }

  // Soft threshold/sensitivity preset chips (RQT / JQL / CPU). Loads numbers only.
  applyPreset({ params }) {
    if (!params.key || params.key === "custom") return

    this.constructor.PRESET_FIELDS.forEach((field) => {
      const raw = params[field]
      if (raw === undefined || raw === null || raw === "") return
      this.#setModelField(field, Number(raw))
    })
    // input events from setModelField already call syncPresetHighlight.
  }

  // Soft ratio starter-estimate chips (RPM / JQS). Loads ratio only.
  applyRatioBand({ params }) {
    if (!params.key || params.key === "custom") return
    if (params.ratio === undefined || params.ratio === null || params.ratio === "") return

    this.#setModelField("ratio", Number(params.ratio))
  }

  #setModelField(field, value) {
    this[`${field}Value`] = value

    const input = this.element.querySelector(`[data-autoscaler--form-model="${field}"]`)
    if (!input) return

    const next = String(value)
    if (input.value !== next) input.value = next
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // The preset whose fields all match is the current one. Otherwise Custom.
  #syncPresetHighlight() {
    const presets = this.element.querySelector("[data-autoscaler--form-preset-group]")
    if (presets) {
      this.#showOption(presets, this.#matchedPresetKey(presets, "data-autoscaler--form-preset-key"))
      return
    }

    const bands = this.element.querySelector("[data-autoscaler--form-ratio-band-group]")
    if (bands) this.#showOption(bands, this.#matchedRatioBandKey(bands))
  }

  #matchedPresetKey(group, keyAttribute) {
    const chips = group.querySelectorAll(`[${keyAttribute}]`)
    for (const chip of chips) {
      const key = chip.getAttribute(keyAttribute)
      if (!key || key === "custom") continue
      if (this.#chipMatchesCurrentFields(chip)) return key
    }
    return "custom"
  }

  #matchedRatioBandKey(group) {
    const chips = group.querySelectorAll("[data-autoscaler--form-ratio-band-key]")
    for (const chip of chips) {
      const key = chip.getAttribute("data-autoscaler--form-ratio-band-key")
      if (!key || key === "custom") continue
      const ratio = chip.getAttribute("data-autoscaler--form-ratio-param")
      if (ratio === null) continue
      if (Number(ratio) === Number(this.ratioValue)) return key
    }
    return "custom"
  }

  #chipMatchesCurrentFields(chip) {
    return this.constructor.PRESET_FIELDS.every((field) => {
      const attr = `data-autoscaler--form-${this.#dasherize(field)}-param`
      const raw = chip.getAttribute(attr)
      if (raw === null) return true // field not part of this preset
      return Number(raw) === Number(this[`${field}Value`])
    })
  }

  #dasherize(camel) {
    return camel.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
  }

  limitDownDescription() {
    if (this.limitDownValue === 0)
      return `No limit.`

    return `Max decrease of ${dynos(pluralize(this.limitDownValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  limitUpDescription() {
    if (this.limitUpValue === 0)
      return `No limit.`

    return `Max increase of ${dynos(pluralize(this.limitUpValue, "{{n}} dyno", "{{n}} dynos"))} per step.`
  }

  sensitivityDownDescription() {
    const lag = this.#sensitivityLagPhrase(this.sensitivityDownValue)
    if (this.sensitivityDownValue === 1)
      return `Scale down ${metric("immediately")} when a checkup calls for it${lag}.`

    return `Scale down after ${metric(pluralize(this.sensitivityDownValue, "{{n}} consecutive checkup", "{{n}} consecutive checkups"))} call for it${lag}.`
  }

  sensitivityUpDescription() {
    const lag = this.#sensitivityLagPhrase(this.sensitivityUpValue)
    if (this.sensitivityUpValue === 1)
      return `Scale up ${metric("immediately")} when a checkup calls for it${lag}.`

    return `Scale up after ${metric(pluralize(this.sensitivityUpValue, "{{n}} consecutive checkup", "{{n}} consecutive checkups"))} call for it${lag}.`
  }

  cooldownDownDescription() {
    if (!this.cooldownDownValue || this.cooldownDownValue === 0)
      return `${metric("No downscale cooldown")}. Scale down again as soon as sensitivity is met.`

    return `After a downscale, wait ${metric(this.#formatCooldown(this.cooldownDownValue))} before another downscale.`
  }

  cooldownUpDescription() {
    if (!this.cooldownUpValue || this.cooldownUpValue === 0)
      return `${metric("No upscale cooldown")}. Scale up again as soon as sensitivity is met (can stack while dynos boot).`

    return `After an upscale, wait ${metric(this.#formatCooldown(this.cooldownUpValue))} before another upscale so new capacity can boot.`
  }

  #formatCooldown(value) {
    const amount = Number(value) || 0
    if (this.cooldownUnitValue === "minutes") return this.#formatDuration(amount * 60)
    return this.#formatDuration(value)
  }

  #hundredths(value) {
    return (Number(value) / 100).toFixed(2)
  }

  #ordinal(value) {
    const n = Number(value) || 0
    const mod100 = n % 100
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`
    switch (n % 10) {
      case 1: return `${n}st`
      case 2: return `${n}nd`
      case 3: return `${n}rd`
      default: return `${n}th`
    }
  }

  #sensitivityLagPhrase(count) {
    const frequency = this.checkupFrequencyValue
    if (!count || !frequency) return ""

    const seconds = count * frequency
    return ` (~${this.#formatDuration(seconds)} at this app's ${frequency}s checkup frequency)`
  }

  #formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0))
    if (seconds < 60) return `${seconds}s`

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const rem = seconds % 60
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (rem > 0) parts.push(`${rem}s`)
    return parts.join(" ")
  }
}
